import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  formatMoney,
  formatQuantity,
  isoDaysAgo,
  isoToday,
  type TransactionRow,
} from '@greencandle/api-client'
import { api } from '../api'
import { TransactionForm } from '../components/TransactionForm'

const TYPES = [
  'BUY', 'SELL', 'DIVIDEND', 'INTEREST', 'FEE', 'TAX_WITHHOLDING',
  'OPTION_EXERCISE', 'OPTION_ASSIGNMENT', 'OPTION_EXPIRY', 'TRANSFER',
] as const

const RANGES = [
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 365, label: '1y' },
  { days: 0, label: 'All' },
]

/**
 * FR-H4. Every transaction, filtered by date, type and instrument.
 *
 * <p>This is where an import is checked. After committing a statement the question is always
 * "did that write what I expected", and the answer is a list you can narrow - which is why the
 * source column is shown rather than hidden as plumbing: a row that came from a statement and a
 * row entered by hand are different things when a figure looks wrong.
 */
export function TransactionsPage() {
  const [accountId, setAccountId] = useState<string | null>(null)
  const [type, setType] = useState<string>('')
  const [days, setDays] = useState(90)
  const [symbol, setSymbol] = useState('')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<TransactionRow | null>(null)
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: (row: TransactionRow) => api.deleteTransaction(selectedOrThrow(), row.id),
    // Deleting a trade re-derives lots, so everything downstream changes.
    onSuccess: () => void queryClient.invalidateQueries(),
  })

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.listAccounts() })
  const selected = accountId ?? accounts.data?.[0]?.id ?? null

  const rows = useQuery({
    queryKey: ['transactions', selected, type, days],
    queryFn: () =>
      api.transactions(selected!, {
        type: type || undefined,
        from: days === 0 ? undefined : isoDaysAgo(days),
        to: days === 0 ? undefined : isoToday(),
      }),
    enabled: selected !== null,
    placeholderData: (previous) => previous,
  })

  const account = accounts.data?.find((a) => a.id === selected) ?? accounts.data?.[0]

  function selectedOrThrow(): string {
    if (!selected) throw new Error('no account selected')
    return selected
  }

  if (accounts.isPending) return <p className="muted">Loading…</p>
  if (accounts.isError) return <Failed error={accounts.error} />
  if (!account) {
    return (
      <>
        <h1 className="page-title">Transactions</h1>
        <div className="notice">No accounts yet.</div>
      </>
    )
  }

  // Symbol narrowing is done here rather than server-side: the server filters by instrument id,
  // and typing a ticker is what a person actually does. The list is already scoped to one
  // account and a date range by then, so there is nothing large to sift.
  const filtered = (rows.data ?? []).filter(
    (r) => symbol === '' || (r.symbol ?? '').toUpperCase().includes(symbol.toUpperCase()),
  )

  return (
    <>
      <h1 className="page-title">Transactions</h1>
      <p className="page-subtitle">{account.name}</p>

      <div className="filters">
        <select aria-label="Account" value={account.id}
                onChange={(e) => setAccountId(e.target.value)}>
          {accounts.data.map((a) => (
            <option key={a.id} value={a.id}>{a.name} ({a.baseCurrency})</option>
          ))}
        </select>

        <div className="segmented" role="group" aria-label="Date range">
          {RANGES.map((r) => (
            <button key={r.days} type="button" aria-pressed={days === r.days}
                    onClick={() => setDays(r.days)}>
              {r.label}
            </button>
          ))}
        </div>

        <select aria-label="Type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <input
          aria-label="Symbol"
          placeholder="Symbol"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          className="filter-input"
        />

        <span className="muted">
          {filtered.length} row{filtered.length === 1 ? '' : 's'}
        </span>

        <button type="button" className="primary" onClick={() => { setEditing(null); setAdding(true) }}>
          Add transaction
        </button>
      </div>

      {(adding || editing) && (
        <TransactionForm
          accountId={account.id}
          existing={editing ?? undefined}
          onDone={() => { setAdding(false); setEditing(null) }}
        />
      )}

      {rows.isError ? (
        <Failed error={rows.error} />
      ) : (
        <div className={`card${rows.isFetching && !rows.isPending ? ' refetching' : ''}`}>
          {filtered.length === 0 ? (
            <div className="notice">Nothing matches these filters.</div>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Symbol</th>
                    <th className="right">Quantity</th>
                    <th className="right">Price</th>
                    <th className="right">Fees</th>
                    <th className="right">Amount</th>
                    <th>Source</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <Row
                      key={row.id}
                      row={row}
                      onEdit={() => { setAdding(false); setEditing(row) }}
                      onDelete={() => remove.mutate(row)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}

function Row({
  row,
  onEdit,
  onDelete,
}: {
  row: TransactionRow
  onEdit: () => void
  onDelete: () => void
}) {
  const buy = row.type === 'BUY'
  const sell = row.type === 'SELL'

  return (
    <tr>
      <td className="num">{row.tradeDate}</td>
      <td>
        <span className={`pill${buy ? ' ok' : sell ? ' bad' : ''}`}>
          {row.type.replace(/_/g, ' ')}
        </span>
      </td>
      <td>{row.symbol ?? <span className="faint">—</span>}</td>
      <td className={`right num${buy ? ' up' : sell ? ' down' : ''}`}>
        {formatQuantity(row.quantity) ?? '—'}
      </td>
      <td className="right num">{formatMoney(row.unitPrice, row.currency) ?? '—'}</td>
      <td className="right num">
        {row.fees && Number(row.fees) !== 0 ? formatMoney(row.fees, row.currency) : '—'}
      </td>
      <td className="right num">{formatMoney(row.amount, row.currency) ?? '—'}</td>
      <td className="faint">{row.source ?? 'manual'}</td>
      <td className="right" style={{ whiteSpace: 'nowrap' }}>
        <button type="button" onClick={onEdit}>Edit</button>{' '}
        <button type="button" onClick={onDelete}>Delete</button>
      </td>
    </tr>
  )
}

function Failed({ error }: { error: unknown }) {
  return (
    <div className="notice error" role="alert">
      {error instanceof ApiError
        ? (error.problem.detail ?? error.message)
        : 'Could not reach the API.'}
    </div>
  )
}
